import React from 'react';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';

/**
 * Reusable OTP-entry dialog for SuperAdmin module saves.
 *
 * Adapts to the channel rules returned by the backend
 * `POST /admin/{module}/request-otp` endpoint:
 *   - `requiredChannels=['email']`                       → one input
 *   - `requiredChannels=['email','whatsapp']`            → two inputs
 *   - `whatsappFallback=true` (carrier rejection, etc.)  → email-only +
 *     amber fallback banner explaining why WhatsApp was skipped.
 *
 * The verify button is disabled until every required code is six digits.
 */
export function AdminOtpDialog({
  open,
  onOpenChange,
  emailOtp,
  setEmailOtp,
  whatsappOtp,
  setWhatsappOtp,
  requiredChannels = ['email'],
  whatsappFallback = false,
  fallbackReason = '',
  emailMasked = '',
  whatsappMasked = '',
  verifying = false,
  onVerify,
  title = 'Confirm with OTP',
  description = 'For security, this admin action needs OTP verification.',
}) {
  const needsWhatsapp = requiredChannels.includes('whatsapp');
  const canVerify =
    emailOtp.length === 6 && (!needsWhatsapp || whatsappOtp.length === 6);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-otp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-500" /> {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {whatsappFallback && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-start gap-2"
              data-testid="admin-otp-fallback-alert"
              role="alert"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">WhatsApp delivery unavailable</p>
                <p className="text-xs mt-0.5">
                  {fallbackReason || 'Falling back to secure Email OTP verification.'}
                </p>
              </div>
            </div>
          )}

          <p className="text-sm text-slate-600">
            {needsWhatsapp ? (
              <>
                We&apos;ve sent codes to <strong>{emailMasked || 'your email'}</strong>{' '}
                and WhatsApp ({whatsappMasked || 'your number'}). Enter both below.
              </>
            ) : (
              <>
                We&apos;ve sent a 6-digit code to{' '}
                <strong>{emailMasked || 'your email'}</strong>. Please enter it below.
              </>
            )}
          </p>

          <div className="space-y-2">
            <Label>Email OTP</Label>
            <Input
              type="text"
              maxLength={6}
              value={emailOtp}
              onChange={(e) =>
                setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="000000"
              className="text-center text-lg tracking-widest"
              autoComplete="one-time-code"
              data-testid="admin-otp-email-input"
            />
          </div>

          {needsWhatsapp && (
            <div className="space-y-2">
              <Label>WhatsApp OTP</Label>
              <Input
                type="text"
                maxLength={6}
                value={whatsappOtp}
                onChange={(e) =>
                  setWhatsappOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="000000"
                className="text-center text-lg tracking-widest"
                autoComplete="one-time-code"
                data-testid="admin-otp-whatsapp-input"
              />
              {whatsappMasked && (
                <p className="text-xs text-slate-500">Sent to {whatsappMasked}</p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={onVerify}
              disabled={!canVerify || verifying}
              className="flex-1"
              data-testid="admin-otp-verify-btn"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…
                </>
              ) : (
                <>Verify &amp; Save</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={verifying}
              data-testid="admin-otp-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AdminOtpDialog;
